// The DD ACCOUNT ROSTER helpers (ADR-0037) shared by the admin and seller
// surfaces: reading a roster question's prompt, naming an account, and the one
// call that returns a FULL account number. No React here — the apps wrap the
// list/add/edit/merge calls in their own react-query hooks.
import { api } from './client';
import type { DdAccountKind, DdRequest, RevealDdAccountNumberResponse } from './types';

/** Each kind's placeholder token and the noun it reads as (mirrors the API's
 * ACCOUNT_KINDS, src/modules/dd/account-candidates.ts). */
export const DD_ACCOUNT_KIND_WORDS: Record<
  DdAccountKind,
  { token: string; noun: string; plural: string }
> = {
  bank_account: { token: '[Bank Account]', noun: 'bank account', plural: 'Bank accounts' },
  credit_card: { token: '[Credit Card]', noun: 'credit card', plural: 'Credit cards' },
  line_of_credit: {
    token: '[Line of Credit]',
    noun: 'line of credit',
    plural: 'Lines of credit',
  },
  loan: {
    token: '[Loan / Note Payable]',
    noun: 'loan or note payable',
    plural: 'Loans and notes payable',
  },
  merchant_account: {
    token: '[Merchant Account]',
    noun: 'merchant account',
    plural: 'Merchant accounts',
  },
};

const KINDS = Object.keys(DD_ACCOUNT_KIND_WORDS) as DdAccountKind[];

function tokenKey(token: string): string {
  return token.replace(/[[\]]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** The state placeholder ("[State]", issue #157) — not an account kind. */
const STATE_TOKEN_KEY = 'state';

const KIND_BY_TOKEN = new Map<string, DdAccountKind>(
  KINDS.map((kind) => [tokenKey(DD_ACCOUNT_KIND_WORDS[kind].token), kind]),
);

/**
 * A roster question's prompt with its kind tokens read as words ("List every
 * [Bank Account] …" → "List every bank account …"). Unknown bracketed text is
 * left as it is. Pure.
 */
export function readableRosterPrompt(prompt: string): string {
  return prompt.replace(/\[[^[\]]+\]/g, (token) => {
    if (tokenKey(token) === STATE_TOKEN_KEY) return 'state';
    const kind = KIND_BY_TOKEN.get(tokenKey(token));
    return kind ? DD_ACCOUNT_KIND_WORDS[kind].noun : token;
  });
}

/**
 * A checklist category name as a seller reads it: an account PLACEHOLDER
 * ("[Bank Account]") becomes its kind's plural ("Bank accounts"); the older
 * single "[Account Name]" token becomes "Accounts". Any other name is unchanged.
 */
export function readableCategoryName(name: string): string {
  if (!/^\s*\[[^[\]]+\]\s*$/.test(name)) return name;
  if (tokenKey(name) === STATE_TOKEN_KEY) return 'States';
  const kind = KIND_BY_TOKEN.get(tokenKey(name));
  return kind ? DD_ACCOUNT_KIND_WORDS[kind].plural : 'Accounts';
}

/** The kind a prompt names when it names exactly one kind token, else null —
 * the API's own rule for `rosterKind` (used by the demo mocks). Pure. */
export function rosterKindOfPrompt(prompt: string): DdAccountKind | null {
  const kinds = new Set<DdAccountKind>();
  for (const m of prompt.matchAll(/\[[^[\]]+\]/g)) {
    const kind = KIND_BY_TOKEN.get(tokenKey(m[0]));
    if (kind) kinds.add(kind);
  }
  return kinds.size === 1 ? [...kinds][0]! : null;
}

/** An account as people read it: its name plus "••1234" when the last 4 are
 * known (and the name does not already carry them). */
export function accountDisplayName(account: { label: string; last4: string | null }): string {
  const { label, last4 } = account;
  if (!last4 || label.includes(`••${last4}`)) return label;
  return `${label} ••${last4}`;
}

/** Whether a checklist item stands for "one category per account" (ADR-0037). */
export function isAccountPlaceholderItem(req: Pick<DdRequest, 'accountPlaceholder'>): boolean {
  return req.accountPlaceholder === true;
}

/** Whether a checklist item stands for "one category per state" (issue #157). */
export function isStatePlaceholderItem(req: Pick<DdRequest, 'statePlaceholder'>): boolean {
  return req.statePlaceholder === true;
}

/** "States: none chosen yet", or "States: Ohio, Texas". */
export function placeholderStatesLine(req: Pick<DdRequest, 'states'>): string {
  const states = req.states ?? [];
  if (states.length === 0) return 'States: none chosen yet';
  return `States: ${states.map((s) => s.name).join(', ')}`;
}

/** "Accounts: none identified yet", or "Accounts: Chase Operations ••1234, …"
 * (for a "[State]" item, its states — {@link placeholderStatesLine}). */
export function placeholderAccountsLine(
  req: Pick<DdRequest, 'accounts'> & Partial<Pick<DdRequest, 'statePlaceholder' | 'states'>>,
): string {
  if (req.statePlaceholder === true) return placeholderStatesLine(req);
  const accounts = req.accounts ?? [];
  if (accounts.length === 0) return 'Accounts: none identified yet';
  return `Accounts: ${accounts.map(accountDisplayName).join(', ')}`;
}

/**
 * A checklist category heading. An account PLACEHOLDER ("[Bank Account]") reads
 * as words plus where it stands: "Bank accounts — none identified yet", or
 * "Bank accounts — Chase Operations ••1234, Frost Payroll" (the accounts of its
 * items). Any other category is its name, unchanged — never a raw token.
 */
export function categoryHeading(
  name: string,
  items: readonly (Pick<DdRequest, 'accountPlaceholder' | 'accounts'> &
    Partial<Pick<DdRequest, 'statePlaceholder' | 'states'>>)[],
): string {
  const readable = readableCategoryName(name);
  if (readable === name) return name;
  if (tokenKey(name) === STATE_TOKEN_KEY) {
    const states = items.find((i) => i.statePlaceholder === true)?.states ?? [];
    return states.length === 0
      ? 'States — none chosen yet'
      : `States — ${states.map((s) => s.name).join(', ')}`;
  }
  const accounts = items.find((i) => i.accountPlaceholder === true)?.accounts ?? [];
  return accounts.length === 0
    ? `${readable} — none identified yet`
    : `${readable} — ${accounts.map(accountDisplayName).join(', ')}`;
}

/** Digits only ("1234 5678-90" → "1234567890"). */
export function accountNumberDigits(value: string): string {
  return value.replace(/[\s-]/g, '');
}

/**
 * POST /rooms/:roomId/dd-accounts/:accountId/reveal-number — the FULL account
 * number. Audited server-side. Call it only from an explicit "Show full number"
 * click; keep the result only while it is on screen, never cache or log it. A
 * plain call (not a cached query/mutation) so no store keeps the value.
 */
export async function revealDdAccountNumber(roomId: string, accountId: string): Promise<string> {
  const res = await api.post<RevealDdAccountNumberResponse>(
    `/rooms/${roomId}/dd-accounts/${accountId}/reveal-number`,
  );
  return res.accountNumber;
}
